import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { DashboardLayout } from '@/components/DashboardLayout';
import { Input } from '@/components/ui/input';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import api from '@/lib/api';
import { Warehouse, Scissors, Shirt, CheckSquare, Clock, User, ExternalLink } from 'lucide-react';

const DEPT_TABS = [
  { key: 'godown', label: 'Godown', icon: Warehouse },
  { key: 'cutting', label: 'Cutting', icon: Scissors },
  { key: 'stitching', label: 'Stitching', icon: Shirt },
  { key: 'finishing', label: 'Finishing', icon: CheckSquare },
];

export default function FloorDataPage() {
  const navigate = useNavigate();
  const [activeDept, setActiveDept] = useState('godown');
  const [floorData, setFloorData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [factories, setFactories] = useState([]);
  const [selectedFactory, setSelectedFactory] = useState('');
  const [month, setMonth] = useState(() => new Date().toISOString().slice(0, 7));
  const [logsModal, setLogsModal] = useState(null);
  const [logs, setLogs] = useState([]);

  useEffect(() => {
    const init = async () => {
      try {
        const [factRes, profileRes] = await Promise.all([api.get('/factories'), api.get('/profile')]);
        setFactories(factRes.data);
        if (profileRes.data.factory_id) setSelectedFactory(profileRes.data.factory_id);
        else if (factRes.data.length > 0) setSelectedFactory(factRes.data[0].id);
      } catch (e) { console.error(e); }
    };
    init();
  }, []);

  const loadFloor = useCallback(async () => {
    if (!selectedFactory) return;
    setLoading(true);
    try {
      const res = await api.get(`/floor/${selectedFactory}?month=${month}`);
      setFloorData(res.data);
    } catch (e) { console.error(e); }
    setLoading(false);
  }, [selectedFactory, month]);

  useEffect(() => { loadFloor(); }, [loadFloor]);

  const basePath = window.location.pathname.includes('/internal/') ? '/dashboard/internal/floor' : '/dashboard/admin/floor';
  const goToDetail = (orderId) => navigate(`${basePath}/${orderId}?factory=${selectedFactory}&month=${month}`);
  const tot = (arr, key) => (arr || []).reduce((s, r) => s + (r[key] || 0), 0);
  const factoryName = factories.find(f => f.id === selectedFactory)?.name || '';
  const latestDate = floorData?.latest_date || '';

  const openLogs = async (orderId, pa) => {
    try { const res = await api.get(`/floor/logs/${orderId}`); setLogs(res.data); } catch { setLogs([]); }
    setLogsModal(pa);
  };

  return (
    <DashboardLayout>
      <div data-testid="floor-data-page" className="space-y-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="font-heading text-2xl sm:text-3xl font-semibold text-[#0A0A0A] tracking-tight">Live Floor Data</h1>
            <p className="text-sm text-[#52525B] mt-1">
              {factoryName || 'Select a factory'}
              {latestDate && <span className="ml-2 text-[#A1A1AA]">Latest DPR: {latestDate}</span>}
            </p>
          </div>
          <div className="flex items-center gap-3">
            {factories.length > 1 && (
              <select value={selectedFactory} onChange={(e) => setSelectedFactory(e.target.value)} className="h-10 rounded-md border border-black/[0.08] px-3 text-sm bg-white" data-testid="floor-factory-select">
                {factories.map(f => <option key={f.id} value={f.id}>{f.name}</option>)}
              </select>
            )}
            <Input type="month" value={month} onChange={(e) => setMonth(e.target.value)} className="h-10 w-[160px] rounded-md text-sm" data-testid="floor-month-input" />
          </div>
        </div>

        <div className="flex items-center gap-1 bg-white rounded-md border border-black/[0.06] p-1 w-fit">
          {DEPT_TABS.map(tab => (
            <button key={tab.key} onClick={() => setActiveDept(tab.key)}
              className={`flex items-center gap-2 px-4 py-2.5 rounded-md text-sm font-medium transition-colors ${activeDept === tab.key ? 'bg-[#1C1C1C] text-white' : 'text-[#52525B] hover:bg-[#F5F5F5]'}`}
              data-testid={`floor-tab-${tab.key}`}>
              <tab.icon className="w-4 h-4" />{tab.label}
            </button>
          ))}
        </div>

        {loading ? (
          <div className="flex justify-center py-20"><div className="w-6 h-6 border-2 border-[#1C1C1C] border-t-transparent rounded-full animate-spin" /></div>
        ) : (
          <>
            {/* GODOWN */}
            {activeDept === 'godown' && (
              <div className="space-y-6" data-testid="godown-tv-section">
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                  <TVCard label="Fabric Received" value={tot(floorData?.godown, 'received')} color="text-emerald-600" bg="bg-emerald-50/50" />
                  <TVCard label="Checked" value={tot(floorData?.godown, 'checked')} color="text-blue-600" bg="bg-blue-50/50" />
                  <TVCard label="Relaxed" value={tot(floorData?.godown, 'relaxed')} color="text-violet-600" bg="bg-violet-50/50" />
                  <TVCard label="Issued" value={tot(floorData?.godown, 'issued')} color="text-amber-600" bg="bg-amber-50/50" />
                </div>
                <TVTable data={floorData?.godown} prefix="godown" onPAClick={goToDetail} onLogClick={openLogs}
                  cols={[
                    { k: 'received', l: 'Received', c: 'text-emerald-600' },
                    { k: 'checked', l: 'Checked', c: 'text-blue-600' },
                    { k: 'relaxed', l: 'Relaxed', c: 'text-violet-600' },
                  ]} />
              </div>
            )}

            {/* CUTTING */}
            {activeDept === 'cutting' && (
              <div className="space-y-6" data-testid="cutting-tv-section">
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                  <TVCard label="Received" value={tot(floorData?.cutting, 'received')} color="text-[#0A0A0A]" bg="bg-[#F4F4F5]/60" sub="From Godown" />
                  <TVCard label="Cut Today" value={tot(floorData?.cutting, 'cut_today')} color="text-blue-600" bg="bg-blue-50/50" sub="Latest DPR" />
                  <TVCard label="Sent to Stitching" value={tot(floorData?.cutting, 'sent')} color="text-amber-600" bg="bg-amber-50/50" />
                  <TVCard label="Pending" value={tot(floorData?.cutting, 'pending')} color="text-red-600" bg="bg-red-50/50" />
                </div>
                <TVTable data={floorData?.cutting} prefix="cutting" onPAClick={goToDetail} onLogClick={openLogs}
                  cols={[
                    { k: 'cut_today', l: 'Cut Today', c: 'text-blue-600' },
                    { k: 'cut', l: 'Total Cut', c: 'text-[#0A0A0A]' },
                    { k: 'sent', l: 'Sent', c: 'text-amber-600' },
                  ]} />
              </div>
            )}

            {/* STITCHING */}
            {activeDept === 'stitching' && (
              <div className="space-y-6" data-testid="stitching-tv-section">
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                  <TVCard label="Received" value={tot(floorData?.stitching, 'received')} color="text-[#0A0A0A]" bg="bg-[#F4F4F5]/60" sub="From Cutting" />
                  <TVCard label="Stitched Today" value={tot(floorData?.stitching, 'stitched_today')} color="text-amber-600" bg="bg-amber-50/50" sub="Latest DPR" />
                  <TVCard label="Sent to Finishing" value={tot(floorData?.stitching, 'sent')} color="text-teal-600" bg="bg-teal-50/50" />
                  <TVCard label="Pending" value={tot(floorData?.stitching, 'pending')} color="text-red-600" bg="bg-red-50/50" />
                </div>
                <TVTable data={floorData?.stitching} prefix="stitching" onPAClick={goToDetail} onLogClick={openLogs}
                  cols={[
                    { k: 'stitched_today', l: 'Stitched Today', c: 'text-amber-600' },
                    { k: 'stitched', l: 'Total', c: 'text-[#0A0A0A]' },
                    { k: 'sent', l: 'Sent', c: 'text-teal-600' },
                  ]} />
              </div>
            )}

            {/* FINISHING */}
            {activeDept === 'finishing' && (
              <div className="space-y-6" data-testid="finishing-tv-section">
                <div className="grid grid-cols-3 gap-4">
                  <TVCard label="Received" value={tot(floorData?.finishing, 'received')} color="text-[#0A0A0A]" bg="bg-[#F4F4F5]/60" sub="From Stitching" />
                  <TVCard label="Finished Today" value={tot(floorData?.finishing, 'finished_today')} color="text-emerald-600" bg="bg-emerald-50/50" sub="Latest DPR" />
                  <TVCard label="Pending" value={tot(floorData?.finishing, 'pending')} color="text-red-600" bg="bg-red-50/50" />
                </div>
                <TVTable data={floorData?.finishing} prefix="finishing" onPAClick={goToDetail} onLogClick={openLogs}
                  cols={[
                    { k: 'finished_today', l: 'Finished Today', c: 'text-emerald-600' },
                    { k: 'finished', l: 'Total', c: 'text-[#0A0A0A]' },
                  ]} />
              </div>
            )}
          </>
        )}
      </div>

      {/* Logs Modal */}
      <Dialog open={!!logsModal} onOpenChange={() => setLogsModal(null)}>
        <DialogContent className="max-w-lg" data-testid="floor-logs-modal">
          <DialogHeader><DialogTitle className="text-base font-semibold">Activity Logs — {logsModal}</DialogTitle></DialogHeader>
          <div className="max-h-80 overflow-y-auto space-y-2 mt-2">
            {logs.length === 0 ? <p className="text-sm text-[#A1A1AA] text-center py-6">No logs yet</p> : logs.map((log, i) => (
              <div key={log.id || i} className="flex items-start gap-3 text-xs py-2.5 border-b border-black/[0.03] last:border-0" data-testid={`modal-log-${i}`}>
                <div className="w-6 h-6 rounded-full bg-[#F4F4F5] flex items-center justify-center flex-shrink-0 mt-0.5"><User className="w-3 h-3 text-[#A1A1AA]" /></div>
                <div className="flex-1 min-w-0">
                  <p className="text-[#0A0A0A]"><span className="font-semibold">{log.user_name}</span> updated <span className="font-medium capitalize text-[#52525B]">{log.department}</span></p>
                  <p className="text-[#A1A1AA] mt-0.5">{log.details}</p>
                </div>
                <span className="text-[10px] text-[#A1A1AA] flex-shrink-0 inline-flex items-center gap-1"><Clock className="w-3 h-3" />{log.timestamp ? new Date(log.timestamp).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }) : '-'}</span>
              </div>
            ))}
          </div>
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
}

function TVCard({ label, value, color, bg, sub }) {
  return (
    <div className={`${bg} rounded-xl border border-black/[0.04] p-5 lg:p-6 flex flex-col items-center justify-center min-h-[140px] lg:min-h-[180px] overflow-hidden`} data-testid={`tv-card-${label.toLowerCase().replace(/\s/g, '-')}`}>
      <p className="text-[10px] sm:text-xs font-semibold uppercase tracking-widest text-[#71717A] mb-1 text-center">{label}</p>
      <p className={`font-heading font-bold leading-none ${color} text-center w-full`} style={{ fontSize: 'clamp(48px, 8vw, 100px)' }}>
        {(value || 0).toLocaleString()}
      </p>
      {sub && <p className="text-[9px] text-[#A1A1AA] mt-1 uppercase tracking-wider">{sub}</p>}
    </div>
  );
}

function TVTable({ data = [], prefix, onPAClick, onLogClick, cols }) {
  return (
    <div className="bg-white rounded-md border border-black/[0.06] overflow-hidden">
      <Table>
        <TableHeader>
          <TableRow className="bg-[#FAFAF8]">
            <TableHead className="text-[11px] font-semibold uppercase tracking-wide text-[#A1A1AA]">PA</TableHead>
            {cols.map(col => <TableHead key={col.k} className="text-[11px] font-semibold uppercase tracking-wide text-[#A1A1AA] text-right">{col.l}</TableHead>)}
            <TableHead className="text-[11px] font-semibold uppercase tracking-wide text-[#A1A1AA] text-center">Logs</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {data.map(row => (
            <TableRow key={row.order_id} className="border-b border-black/[0.03]" data-testid={`${prefix}-row-${row.order_id}`}>
              <TableCell>
                <button onClick={() => onPAClick(row.order_id)} className="text-left group">
                  <span className="text-sm font-semibold text-[#0A0A0A] group-hover:underline">{row.pa}</span>
                  <span className="text-[11px] text-[#A1A1AA] ml-2">{row.style}</span>
                </button>
              </TableCell>
              {cols.map(col => (
                <TableCell key={col.k} className="text-right">
                  <span className={`font-heading text-xl font-bold ${col.c}`}>{(row[col.k] || 0).toLocaleString()}</span>
                </TableCell>
              ))}
              <TableCell className="text-center">
                <button onClick={(e) => { e.stopPropagation(); onLogClick(row.order_id, row.pa); }} className="inline-flex items-center gap-1 text-xs text-[#52525B] hover:text-[#0A0A0A] transition-colors" data-testid={`${prefix}-log-btn-${row.order_id}`}>
                  {row.last_updated || row.updated_at ? <><Clock className="w-3 h-3" />{(row.last_updated || row.updated_at).slice(0, 10)}</> : '-'}
                  <ExternalLink className="w-3 h-3 ml-0.5" />
                </button>
              </TableCell>
            </TableRow>
          ))}
          {data.length === 0 && <TableRow><TableCell colSpan={cols.length + 2} className="text-center py-10 text-sm text-[#A1A1AA]">No active orders</TableCell></TableRow>}
        </TableBody>
      </Table>
    </div>
  );
}
